/**
 * iter304 · Shared referral-code utilities
 * ─────────────────────────────────────────────────────────────────────
 * Single source of truth for the FLYT Ambassador referral flow. Used
 * by:
 *   • /r/:code redirect page (stashes on click-through)
 *   • Global <ReferralCodeCatcher /> mounted in App.js (stashes any
 *     `?ref=CODE` query on any route)
 *   • LoginModal signup + login forms (visible input the user can
 *     paste into)
 *   • UserAuthContext post-auth hook (reads + consumes + POSTs to
 *     `/api/ambassador/attribute` right after any successful auth)
 *
 * Storage keys are namespaced under `flyt_ref_*` so they never collide
 * with unrelated app state.
 */

const REF_STORAGE_KEY = 'flyt_ref_code';
const REF_STORAGE_TS  = 'flyt_ref_code_ts';
const REF_COOKIE_KEY  = 'flyt_ref';
const REF_TTL_DAYS    = 30;

/** Uppercase, alphanumeric, 4-12 chars. */
export function isValidCode(code) {
  if (!code || typeof code !== 'string') return false;
  const c = code.trim().toUpperCase();
  return /^[A-Z0-9]{4,12}$/.test(c);
}

export function stashReferralCode(rawCode) {
  const code = String(rawCode || '').trim().toUpperCase();
  if (!isValidCode(code)) return false;
  try {
    localStorage.setItem(REF_STORAGE_KEY, code);
    localStorage.setItem(REF_STORAGE_TS, String(Date.now()));
    const maxAge = 60 * 60 * 24 * REF_TTL_DAYS;
    document.cookie = `${REF_COOKIE_KEY}=${encodeURIComponent(code)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  } catch (_) { /* private mode — non-fatal */ }
  return true;
}

/** Prefer URL `?ref=` (freshest), fall back to localStorage. */
export function readReferralCode() {
  try {
    const fromUrl = (new URLSearchParams(window.location.search).get('ref') || '').trim().toUpperCase();
    if (isValidCode(fromUrl)) return fromUrl;
    const fromStore = (localStorage.getItem(REF_STORAGE_KEY) || '').trim().toUpperCase();
    if (isValidCode(fromStore)) return fromStore;
  } catch (_) { /* SSR / private mode */ }
  return null;
}

/** Called after a successful signup so we don't re-attribute the same
 *  device to a later, unrelated account. */
export function clearReferralCode() {
  try {
    localStorage.removeItem(REF_STORAGE_KEY);
    localStorage.removeItem(REF_STORAGE_TS);
    document.cookie = `${REF_COOKIE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch (_) { /* ignore */ }
}
